import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useContextualRouting } from 'next-use-contextual-routing';
import { useMount, useUnmount } from 'react-use';
import { Box } from '@mui/material';
import { trackNavigatedToPage, slugify } from '@surfline/web-common';
import { getEntitlements, getUserDetails, getUserSettings } from 'selectors/user';
import theme from '@wavetrak/theme';

import { doSetRecentlyVisited } from 'actions/user';
import Breadcrumbs from 'components/Breadcrumbs';
import { PremiumSpotUpgradePill } from 'components/CTAs';
import NearbyBuoys from 'components/NearbyBuoys';
import PageLoading from 'components/PageLoading';
import SpotPageMeta from 'components/SpotPageMeta';
import SpotTravelInfoSummary from 'components/SpotTravelInfoSummary';
import SpotPageMiddleAd from 'components/SpotPageMiddleAd';
import LiveWindSourceAttribution from 'components/LiveWindSourceAttribution/LiveWindSourceAttribution';
import type { LiveWindAttributionSources } from 'components/LiveWindSourceAttribution/LiveWindSourceAttribution';
import ModalContextual from 'components/ModalContextual';
import config from 'config';
import ForecastDataVisualizer from 'containers/ForecastDataVisualizer';
import SpotCurrentConditionsSection from 'containers/SpotCurrentConditionsSection';
import SubregionForecast from 'containers/SubregionForecast';
import RateSurf from 'containers/RateSurf/RateSurf';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { useSpotDataSync } from 'hooks/useSpotDataSync';
import useSpotPageScroll from 'containers/ForecastDataVisualizer/hooks/useSpotPageScroll';
import {
  getSpotDetails,
  getSpotLoading,
  getSpotReport,
  getSpotReportAssociated,
  getSpotReportForecast,
  getTravelDetails,
  getConditionsBasedAdUnits,
} from 'selectors/spot';
import { getSubregionLoading } from 'selectors/subregion';
import { useUserPermissionStatus } from 'selectors/user';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import type { TravelDetails } from 'types/travelDetails';
import type { UserDetails, UserEntitlements, UserSettings } from 'types/user';
import type { SpotDetails, SpotReport, SpotReportAssociated } from 'types/spot';
import resolveBreadcrumbs from 'utils/urls/breadcrumbResolver';
import { URLStateProvider } from 'contexts/URLStateContext';
import convertConditionsBasedAdUnitsToAdTargets from 'utils/conditionsBasedAdUnits';
import { initializeBlogherads } from 'utils/blogherads';

const SpotPage: FunctionComponent = () => {
  const entitlements: UserEntitlements = useAppSelector(getEntitlements);
  const {
    hasAdFreePermissions,
    hasPremiumCamPermissions,
    hasCoreForecastPermissions,
    hasPremiumAnalysisPermissions,
  } = useUserPermissionStatus();
  const isEntitled = entitlements.length > 0;
  // Scoped grants (e.g. subregion partnerships) give premium access to specific
  // spots without a paid entitlement — don't upsell where the grant applies.
  const hasScopedPremiumAccess = hasCoreForecastPermissions && hasPremiumAnalysisPermissions;
  const report: SpotReport['report'] = useAppSelector(getSpotReport);
  const reportData = useAppSelector(getSpotReportForecast);
  const spot: SpotDetails = useAppSelector(getSpotDetails);
  const spotAssociated: SpotReportAssociated = useAppSelector(getSpotReportAssociated);
  const spotLoading: boolean = useAppSelector(getSpotLoading);
  const subRegionLoading: boolean = useAppSelector(getSubregionLoading);
  const travelDetails: TravelDetails = useAppSelector(getTravelDetails);
  const userDetails: Partial<UserDetails> | null = useAppSelector(getUserDetails);
  const userSettings: UserSettings | null = useAppSelector(getUserSettings);
  const conditionsBasedAdUnits = useAppSelector(getConditionsBasedAdUnits);
  const device = useDeviceSize();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const trackPage = useRef(true);
  const segmentPropertiesRef = useRef({});
  const { returnHref, makeContextualHref } = useContextualRouting();
  const { camId, initialStartDayIndex, subregionForecast, rateTheSurf } = router.query as {
    camId?: string;
    initialStartDayIndex?: string;
    subregionForecast?: string;
    rateTheSurf?: string;
  };
  const [isNavigating, setIsNavigating] = useState(false);

  // Use the custom hook to sync spot data when router changes
  useSpotDataSync();

  // Use the custom hook for page scrolling (includes route change scroll-to-top)
  useSpotPageScroll(setIsNavigating);

  const loading: boolean = useMemo(
    () => !!spotLoading || !!subRegionLoading || router.isFallback || isNavigating,
    [spotLoading, subRegionLoading, router.isFallback, isNavigating],
  );

  const urlSetCam = useMemo(
    () => spot && spot.cameras.filter((cam) => cam._id === camId),
    [camId, spot],
  );

  const urlSetCamId = useMemo(
    () => (urlSetCam && urlSetCam.length > 0 ? urlSetCam[0]._id : null),
    [urlSetCam],
  );

  const spotMiddleAdTarget = useMemo(() => {
    const baseTargets = urlSetCamId ? [['camId', urlSetCamId]] : [['spotid', spot?._id]];
    const conditionsTargets = convertConditionsBasedAdUnitsToAdTargets(conditionsBasedAdUnits);
    return [...baseTargets, ...conditionsTargets];
  }, [spot, urlSetCamId, conditionsBasedAdUnits]);
  const hasMultipleCams = useMemo(() => spot?.cameras?.length > 1, [spot?.cameras?.length]);

  const segmentPropsPremiumUpgrade = useMemo(
    () => ({
      location: 'Premium Pill Latest',
      pageName: spot?.name,
      spotId: spot?._id,
      subregionId: spot?.subregion?._id,
    }),
    [spot],
  );

  const userUnits = useMemo(
    () =>
      userSettings?.units
        ? // waveHeight is missing in userSettings.units but required in the Units type
          { ...userSettings.units, waveHeight: userSettings.units.surfHeight }
        : {
            swellHeight: '',
            temperature: '',
            tideHeight: '',
            waveHeight: '',
            windSpeed: '',
          },
    [userSettings],
  );

  const nearbyBuoysLocation = useMemo(
    () => ({
      latitude: spot.lat,
      longitude: spot.lon,
    }),
    [spot],
  );

  const hasSmartCam = useMemo(
    () => spot?.cameras?.some((cam) => cam.supportsInsights && spot?.insightsCameraId),
    [spot?.cameras, spot?.insightsCameraId],
  );

  const breadcrumbs = useMemo(() => resolveBreadcrumbs(spot.breadcrumb), [spot.breadcrumb]);

  // Contextual modal link and path for Rate the Surf
  const rateTheSurfAsPath = useMemo(
    () => `/surf-report/${slugify(spot.name)}/${spot._id}/rate-the-surf`,
    [spot._id, spot.name],
  );

  const contextualRateTheSurfHref = useMemo(
    () => makeContextualHref({ rateTheSurf: 'true' }),
    [makeContextualHref],
  );

  const handleOpenRateTheSurf = useCallback(() => {
    router.push(contextualRateTheSurfHref, rateTheSurfAsPath, { shallow: true });
  }, [router, contextualRateTheSurfHref, rateTheSurfAsPath]);

  const handleNavigatedToPage = useCallback(() => {
    trackNavigatedToPage('Spot Report', segmentPropertiesRef.current);
  }, []);

  const loadSpotPageComplete = useCallback(
    (_: unknown, { shallow }: { shallow: boolean }) => {
      if (!shallow && trackPage.current) {
        // only track page views on non-shallow routing
        handleNavigatedToPage();
        trackPage.current = false;
      }
    },
    [handleNavigatedToPage],
  );

  const shouldTrackOnNavigation = useCallback((url: string, { shallow }: { shallow: boolean }) => {
    trackPage.current = !(
      shallow === false &&
      url.indexOf('/surf-report') === 0 &&
      url.indexOf('spot-guide') > 0
    );
  }, []);

  useEffect(() => {
    const getCamProps = () => {
      const hasCam = !!spot.cameras.length;
      if (!hasCam) return { hasCam };
      const isSingleCam = !(
        hasPremiumCamPermissions &&
        !device.isMobile &&
        spot.cameras.length > 1
      );
      return {
        hasCam,
        isSingleCam,
        premiumCam: spot.cameras[0].isPremium,
        // return first camId if single cam view or list of all spot camIds if multi cam
        camId: isSingleCam ? spot.cameras[0]._id : spot.cameras.map((cam) => cam._id).join(),
        camNamePrimary: spot.cameras[0].title,
      };
    };
    /*
    Store the segment properties in a ref so that we access the latest version of them from within
    loadSpotPageComplete. loadSpotPageComplete is bound to router events when the component is mounted
    and this binds the initial state value through closure. This ends up always sending the original
    spot info even after the spot has changed. Using a ref allows us to work around this.
    */
    segmentPropertiesRef.current = {
      category: 'cams & reports',
      spotId: spot._id,
      spotName: spot.name,
      subregionId: spot.subregion._id,
      condition: reportData.conditions.value,
      human: reportData.conditions.human,
      waveHeightMin: reportData.waveHeight.min,
      waveHeightMax: reportData.waveHeight.max,
      hasSmartCam,
      scrollWheelPresent: hasSmartCam && device.width < theme.breakpoints.values.desktop,
      forecastType:
        router?.query?.view === 'table' ||
        userSettings?.preferredForecastView?.toLowerCase() === 'table'
          ? 'table'
          : 'graph',
      abilityLevels:
        spot.abilityLevels && spot.abilityLevels.length ? spot.abilityLevels.join() : null,
      boardTypes: spot.boardTypes && spot.boardTypes.length ? spot.boardTypes.join() : null,
      isMobileView: device.width <= theme.breakpoints.values.tabletLg,
      hasLiveWind: !!spot?.hasLiveWind,
      ...getCamProps(),
      path: router?.asPath,
      url: `${config.surflineHost}${router?.asPath}`,
    };
  }, [
    device,
    hasSmartCam,
    hasPremiumCamPermissions,
    reportData,
    router.asPath,
    router?.query.view,
    spot,
    userSettings?.preferredForecastView,
  ]);

  useEffect(() => {
    const { _id: spotId } = spot;
    if (userDetails && spot) {
      dispatch(doSetRecentlyVisited('spots', spotId, spot.name));
    }
  }, [dispatch, spot, userDetails]);

  /**
   * Set global spot ad configuration for SHE Media
   */
  useEffect(() => {
    const blogherads = initializeBlogherads();
    blogherads.adq.push(() => {
      blogherads.setTargeting('pub_meta_4', spot._id);
    });
  }, [spot._id]);

  useMount(() => {
    const { _id: spotId } = spot;
    if (userDetails) dispatch(doSetRecentlyVisited('spots', spotId, spot.name));
    trackPage.current = true;
    loadSpotPageComplete(undefined, { shallow: false });
    // Do not track page when navigating to the spot guide from client side routing
    router.events.on('routeChangeStart', shouldTrackOnNavigation);
    // track page views when navigating to new spots
    router.events.on('routeChangeComplete', loadSpotPageComplete);
  });

  useUnmount(() => {
    router.events.off('routeChangeStart', shouldTrackOnNavigation);
    router.events.off('routeChangeComplete', loadSpotPageComplete);
    // Clean up navigation tracking
    trackPage.current = false;
  });

  return (
    <URLStateProvider spotId={spot?._id} spotName={spot?.name} isWavePool={false}>
      <div
        className={classNames({
          'sl-spot-page': true,
          'sl-spot-page__loading': loading,
        })}
      >
        {loading && <PageLoading />}
        {!loading && spot && (
          <div>
            <SpotPageMeta
              spotName={spot.name}
              countryName={spot.breadcrumb[0].name}
              report={report}
              multiCam={hasMultipleCams}
              spotId={spot._id}
              cameras={spot.cameras}
              metaDescription={spot?.metaDescription ?? ''}
              titleTag={spot?.titleTag ?? ''}
            />
            <SpotCurrentConditionsSection
              spotId={spot._id}
              onOpenRateTheSurf={handleOpenRateTheSurf}
            />
            <div className="sl-spot-page__below-the-fold sl-spot-page__below-the-fold__sl-spot-page-v2">
              {!hasAdFreePermissions && (
                <Box className="sl-spot-page__ad-container">
                  <div className="sl-section-container">
                    <SpotPageMiddleAd
                      entitlements={entitlements}
                      spotMiddleAdTarget={spotMiddleAdTarget as [string, string][]}
                      userSettings={userSettings}
                    />
                  </div>
                </Box>
              )}
              <div className="sl-spot-page__below-the-fold__container sl-section-container">
                <div className="sl-spot-report-page__charts-v2" id="forecast">
                  <div data-testid="forecast-graphs-modernization">
                    <ForecastDataVisualizer
                      spotId={spot._id}
                      spotName={spot.name}
                      subregionId={spot?.subregion?._id ?? ''}
                      hasLiveWind={spot.hasLiveWind}
                      supportsInsights={!!spot.insightsCameraId}
                      consistencyEnabled={spot.consistencyEnabled}
                      spectraDataAvailable={spot.spectraDataAvailable}
                      initialStartDayIndex={
                        initialStartDayIndex ? Number(initialStartDayIndex) : undefined
                      }
                      utcOffset={spotAssociated.utcOffset}
                      units={userUnits}
                    />
                  </div>
                </div>
              </div>
              <div className="sl-spot-page__below-the-fold__container sl-section-container">
                <div className="sl-nearby-buoys-wrapper">
                  <NearbyBuoys
                    location={nearbyBuoysLocation}
                    units={userUnits}
                    trackLocation="Spot Report"
                    spotId={spot._id}
                    spotName={spot.name}
                  />
                </div>
                {travelDetails && (
                  <SpotTravelInfoSummary
                    spotId={spot._id}
                    spotName={spot.name}
                    travelDetails={travelDetails}
                  />
                )}
                <Breadcrumbs breadcrumbs={breadcrumbs} />
                {spotAssociated.windStation?.provider && (
                  <LiveWindSourceAttribution
                    source={spotAssociated.windStation.provider as LiveWindAttributionSources}
                  />
                )}
              </div>
              <ModalContextual
                backgroundColor={theme.palette.secondary.main}
                open={!!subregionForecast}
                returnHref={returnHref}
                scrollContainerId="forecaster-content-modal"
              >
                <SubregionForecast
                  scrollContainerId="forecaster-content-modal"
                  isSubregionListVisible={false}
                />
              </ModalContextual>
              <ModalContextual open={!!rateTheSurf} returnHref={returnHref}>
                <RateSurf />
              </ModalContextual>
            </div>
          </div>
        )}
        {!isEntitled && !hasScopedPremiumAccess && (
          <PremiumSpotUpgradePill segmentProperties={segmentPropsPremiumUpgrade} />
        )}
      </div>
    </URLStateProvider>
  );
};

export default SpotPage;
